// paginateReceipt.js
// Calcula pontos seguros de quebra de página para recibos longos.
//
// Por que: html2canvas captura o recibo inteiro como UMA imagem. Quando
// fatiamos em páginas A4 com altura fixa, podemos cortar uma seção (cliente,
// totais, crediário, vencimentos) bem no meio — leitura ruim e PDF feio.
//
// Solução: antes de descartar o clone offscreen, medimos onde cada elemento
// .print-block começa/termina (em px do clone). Depois usamos esses pontos
// como "pontos seguros" — só quebramos página BETWEEN blocks, nunca DENTRO.
//
// Uso:
//   const blocks = measureBlocks(clone);          // ANTES de remover do DOM
//   const plan = planPages(canvas.height, pagePxHeight, blocks, captureScale);
//   plan.forEach(({ startPx, endPx }) => /* slice e adiciona em PDF/print */);

/**
 * Mede a posição (top, bottom) de todos os elementos .print-block dentro do
 * elemento `root`, em pixels do próprio root.
 *
 * Inclui também as linhas <tr> dentro de tabelas (não têm offsetTop em todos
 * os browsers, então calculamos via getBoundingClientRect relativo ao root).
 */
export function measureBlocks(root) {
  if (!root) return [];
  const rootRect = root.getBoundingClientRect();
  const blocks = [];
  const sel = ".print-block, table tbody tr.print-block, table tbody tr";
  root.querySelectorAll(sel).forEach((el) => {
    const r = el.getBoundingClientRect();
    blocks.push({
      top: r.top - rootRect.top,
      bottom: r.bottom - rootRect.top,
    });
  });
  // ordena por top
  blocks.sort((a, b) => a.top - b.top);
  return blocks;
}

/**
 * Dado: altura total do canvas em px, altura máxima por página em px e blocos
 * medidos (em px do DOM original), retorna um array de slices [{startPx, endPx}].
 *
 * Estratégia:
 *  - começa em offset 0.
 *  - candidato ingênuo: offset + pagePx (quanto cabe).
 *  - busca o maior block.bottom <= candidato (em coordenadas do canvas).
 *  - se encontrou, usa ele; senão usa o candidato (segurança: bloco maior que página).
 *  - continua até cobrir todo o canvas.
 *
 * `domScale` = canvas.height / domHeight  (geralmente o `scale` do html2canvas).
 */
export function planPages(canvasHeight, pagePx, domBlocks, domScale = 1) {
  if (canvasHeight <= pagePx) return [{ startPx: 0, endPx: canvasHeight }];

  // converte bottoms dos blocos para coordenadas do canvas
  const blockBottoms = domBlocks
    .map((b) => Math.round(b.bottom * domScale))
    .filter((v) => v > 0 && v <= canvasHeight)
    .sort((a, b) => a - b);

  const plan = [];
  let offset = 0;
  let safety = 0;
  while (offset < canvasHeight && safety++ < 500) {
    const naiveEnd = Math.min(offset + pagePx, canvasHeight);
    if (naiveEnd >= canvasHeight) {
      plan.push({ startPx: offset, endPx: canvasHeight });
      break;
    }
    // procura o maior bottom de bloco <= naiveEnd e > offset
    let safeEnd = 0;
    for (const b of blockBottoms) {
      if (b > offset && b <= naiveEnd) safeEnd = b;
      else if (b > naiveEnd) break;
    }
    // se nenhum bloco couber inteiro (bloco maior que página), corta no
    // candidato ingênuo — melhor cortar do que travar.
    const end = safeEnd > offset + 50 ? safeEnd : naiveEnd;
    plan.push({ startPx: offset, endPx: end });
    offset = end;
  }
  return plan;
}

/**
 * Cria um sub-canvas com a fatia [startPx, endPx) do canvas original,
 * preenchendo o fundo de branco (PDFs/impressão).
 */
export function sliceCanvas(srcCanvas, startPx, endPx) {
  const sliceHeight = endPx - startPx;
  const c = document.createElement("canvas");
  c.width = srcCanvas.width;
  c.height = sliceHeight;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, c.width, c.height);
  ctx.drawImage(srcCanvas, 0, startPx, srcCanvas.width, sliceHeight, 0, 0, srcCanvas.width, sliceHeight);
  return c;
}
